import type {AxiosError} from "axios";

const PANIC_ERROR_TITLES: Record<string, string> = {
  panic_button_disabled: "Panic button is disabled for this company.",
  panic_button_role_denied: "Your role cannot trigger panic alerts.",
  panic_button_property_denied: "This property is not allowed for panic alerts.",
  panic_button_household_denied: "Your household cannot trigger panic alerts.",
  panic_intent_expired: "Panic confirmation expired. Hold again to retry.",
  panic_intent_open: "A panic confirmation is already open on this device.",
  panic_alert_duplicate: "A recent panic alert already exists.",
  panic_intent_forbidden: "You cannot confirm this panic intent.",
  panic_intent_not_found: "Panic intent was not found. Hold again.",
  panic_intent_already_confirmed: "This panic intent was already confirmed.",
  panic_alert_permission_denied: "You do not have permission for this action.",
  panic_alert_use_intent_flow: "Use hold-to-confirm flow to create panic alerts.",
};

function asAxiosError(
  error: unknown,
): AxiosError<{message?: string; error_code?: string; errors?: string[]}> | null {
  if (!error || typeof error !== "object" || !("isAxiosError" in error)) {
    return null;
  }
  return error as AxiosError<{message?: string; error_code?: string; errors?: string[]}>;
}

export function getPanicErrorCode(error: unknown): string | null {
  const axiosError = asAxiosError(error);
  if (!axiosError) {
    return null;
  }
  const code = axiosError.response?.data?.error_code;
  return typeof code === "string" ? code : null;
}

export function getPanicErrorTitle(error: unknown): string {
  const axiosError = asAxiosError(error);
  if (!axiosError) {
    return "Panic action failed.";
  }

  if (axiosError.response?.status === 429) {
    return "Too many requests. Please wait a moment and try again.";
  }

  if (axiosError.response?.status === 422) {
    return "A required property context is missing.";
  }

  const code = getPanicErrorCode(error);
  if (code && PANIC_ERROR_TITLES[code]) {
    return PANIC_ERROR_TITLES[code];
  }

  const message = axiosError.response?.data?.message?.trim();
  if (message) {
    return message;
  }

  return "Panic action failed.";
}

export function isPanicBusinessError(error: unknown): boolean {
  const axiosError = asAxiosError(error);
  if (!axiosError) {
    return false;
  }
  const status = axiosError.response?.status;
  return typeof status === "number" && status >= 400 && status < 500;
}
